import { FILTER_OPTION_SELECTOR } from '../constants/filter-tree';
import { getTemplate } from '../template';
import {
  debounce,
  debug,
  getLimitOptionValues,
  isViewMoreFilterOption,
  manageFilterTreeViewMoreState,
  replacePrefixValue,
  simpleMd5,
  xssFilterOption,
} from '../utils';
import { getSelectedKeysFilter } from '.';
const SELECTED_CLASS = 'boost-sd__filter-option-box-item--selected';

/**
 * @type {import('./index.d').renderOptionBox}
 */
export async function renderOptionBox(context, dom, option, limit) {
  if (!dom) return;
  if (option.values.length === 0) return;

  const selectedKeys = getSelectedKeysFilter(context);

  const classNameUnselected = 'boost-sd__filter-option-item boost-sd__filter-option-box-item';
  const classNameSelected = `${classNameUnselected} ${SELECTED_CLASS}`;

  const [getActionMapping, setActionMapping] = context.useContextState('actionMapping', {});
  const actionMapping = getActionMapping() || {};

  const size = limit && option.values.length > limit ? limit : option.values.length;
  const isXssDisabled = context.app?.xss?.filter?.disabled;
  let values = isXssDisabled ? getLimitOptionValues(option, size) : xssFilterOption(option, size);
  const label = option.label;

  // export customize boostCustomSortValues
  if (typeof window.boostCustomSortOptionValues === 'function') {
    values = window.boostCustomSortOptionValues(option, size);
  }

  const params = {
    context,
    label,
    values,
    option,
    dom,
    selectedKeys,
    classNameSelected,
    classNameUnselected,
    actionMapping,
    setActionMapping,
  };

  manageFilterTreeViewMoreState(params);
}

/**
 * @type {import('./index.d').renderingOptionBoxTemplate}
 */
export const renderingOptionBoxTemplate = async ({
  context,
  option,
  values,
  dom,
  label,
  selectedKeys,
  classNameSelected,
  classNameUnselected,
  actionMapping,
  setActionMapping,
}) => {
  let html = '';

  for (const value of values) {
    let className = classNameUnselected;
    const itemKey = `${option.filterOptionId}-${value.key?.toString()?.toLowerCase()}`;

    if (selectedKeys[itemKey]) {
      className = classNameSelected;
    }

    value.label = replacePrefixValue(option, value);

    const actionId = simpleMd5(`${option.filterOptionId}-${value.key}`);
    const dataAction = `${option.filterOptionId}.${actionId}`;

    const action = {
      optionList: {
        key: option.filterOptionId,
        value: value.key,
        label: option.label,
        filterType: option.filterType,
        selectType: option.selectType,
        displayType: option.displayType,
        valueDisplay: value.label || '',
      },
    };

    // assign mapping event
    actionMapping[option.filterOptionId] = actionMapping[option.filterOptionId] || {};
    actionMapping[option.filterOptionId][actionId] = action;

    html += await context.templateRender(getTemplate(context).filterOptionBoxTemplate, {
      actionId,
      dataAction,
      value,
      label,
      className,
      displayAllValuesInUppercaseForm: option.displayAllValuesInUppercaseForm,
    });
  }

  setActionMapping(actionMapping);
  dom.innerHTML = html;

  // create custom events - after render
  const renderFiltered = new CustomEvent('afterRenderHtmlForFilter');
  window.dispatchEvent(renderFiltered);
};

/**
 * @type {import('./index.d').lazyLoadOptionBox}
 */
export function lazyLoadOptionBox(context, dom, id, option) {
  // Do not lazy load for View More options
  const shouldPrevent = isViewMoreFilterOption(option);

  if (shouldPrevent) return;

  const domMouseMove = dom?.closest(FILTER_OPTION_SELECTOR);

  const mousemoveRender = debounce(() => {
    debug(context);
    renderFullOptionBox(context, dom, id, option);

    domMouseMove.removeEventListener('mousemove', mousemoveRender);
    domMouseMove.removeEventListener('touchstart', mousemoveRender, { passive: true });
  }, 200);

  domMouseMove && domMouseMove.addEventListener('mousemove', mousemoveRender);
  domMouseMove && domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true });
}

/**
 * Render full option box
 * @type {import('./index.d').renderFullOptionBox}
 */
export function renderFullOptionBox(context, dom, id, option) {
  if (
    context.filterTreeViewPort &&
    context.filterTreeViewPort[id] &&
    context.filterTreeViewPort[id].loaded < context.filterTreeViewPort[id].total &&
    context.filterTreeViewPort[id].displayType === 'box'
  ) {
    renderOptionBox(context, dom, option, context.filterTreeViewPort[id].total);
    context.filterTreeViewPort[id] = {
      loaded: context.filterTreeViewPort[id].total,
      total: option.values.length,
      displayType: 'list',
    };
  }
}

/**
 * @type {import('./index.d').addFilterBoxSelectedClass}
 */
export const addFilterBoxSelectedClass = (context, key, value) => {
  const actionId = simpleMd5(`${key}-${value}`);
  const domElement = context.$(`#${actionId}`);
  if (domElement) {
    domElement.parentElement.classList.add(SELECTED_CLASS);
    domElement.setAttribute('aria-checked', 'true');
  }
};

/**
 * @type {import('./index.d').removeFilterBoxSelectedClass}
 */
export const removeFilterBoxSelectedClass = (context, key, value) => {
  const actionId = simpleMd5(`${key}-${value}`);
  const domElement = context.$(`#${actionId}`);
  if (domElement) {
    domElement.parentElement.classList.remove(SELECTED_CLASS);
    domElement.setAttribute('aria-checked', 'false');
  }
};
